import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Home, ChefHat, Users, DollarSign, SlidersHorizontal, Bell, CircleUser } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/site/Logo";
import TodayTab from "@/components/site/admin/TodayTab";
import CreatorsTab from "@/components/site/admin/CreatorsTab";
import ConsumersTab from "@/components/site/admin/ConsumersTab";
import RevenueTab from "@/components/site/admin/RevenueTab";
import ControlsTab from "@/components/site/admin/ControlsTab";
import InboxTab from "@/components/site/admin/InboxTab";
import AccountTab from "@/components/site/admin/AccountTab";

const SESSION_KEY = "dishly_admin_user";

const TABS = [
  { key: "today", label: "Today", icon: Home },
  { key: "creators", label: "Creators", icon: ChefHat },
  { key: "consumers", label: "Consumers", icon: Users },
  { key: "revenue", label: "Revenue", icon: DollarSign },
  { key: "controls", label: "Controls", icon: SlidersHorizontal },
  { key: "inbox", label: "Inbox", icon: Bell },
  { key: "account", label: "Account", icon: CircleUser },
];

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/app-login", { email, password });
      localStorage.setItem("dishly_token", data.token);
      localStorage.setItem(SESSION_KEY, JSON.stringify({ name: data.name, email: data.email }));
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-black/5 bg-white p-8 shadow-xl">
        <Logo />
        <div className="mt-6 flex items-center gap-2 text-charcoal">
          <Lock className="h-4 w-4 text-terracotta" />
          <h1 className="font-display text-xl font-bold">Admin sign in</h1>
        </div>
        <p className="mt-1 text-sm text-charcoal/55">Use your Dishly app admin account.</p>
        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg" placeholder="admin@dinewithdishly.co" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] font-semibold">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-lg" placeholder="••••••••" />
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-6 w-full rounded-lg bg-charcoal py-3 text-sm font-bold text-cream transition-transform hover:scale-[1.01] disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function AdminShell({ onLogout }) {
  const [tab, setTab] = useState("today");
  const [screen, setScreen] = useState(null);

  const go = useCallback((t, s = null) => {
    setTab(t);
    setScreen(s);
    window.scrollTo(0, 0);
  }, []);

  const me = JSON.parse(localStorage.getItem(SESSION_KEY) || "{}");

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-black/5 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="rounded-full bg-charcoal/5 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-charcoal/60">Admin</span>
          </div>
          <span className="text-xs font-semibold text-charcoal/45" data-testid="admin-identity">{me.name}</span>
        </div>
        <nav className="mx-auto max-w-2xl px-2" data-testid="admin-tabs">
          <div className="flex">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => go(t.key)} data-testid={`tab-${t.key}`}
                className={`flex flex-1 flex-col items-center gap-1 border-b-2 py-2.5 text-[11px] font-bold transition-colors ${tab === t.key ? "border-terracotta text-terracotta" : "border-transparent text-charcoal/45 hover:text-charcoal/70"}`}>
                <t.icon className="h-5 w-5" />
                {t.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {tab === "today" && <TodayTab go={go} />}
        {tab === "creators" && <CreatorsTab screen={tab === "creators" ? screen : null} goScreen={(s) => setScreen(s)} />}
        {tab === "consumers" && <ConsumersTab screen={tab === "consumers" ? screen : null} goScreen={(s) => setScreen(s)} />}
        {tab === "revenue" && <RevenueTab />}
        {tab === "controls" && <ControlsTab screen={tab === "controls" ? screen : null} goScreen={(s) => setScreen(s)} go={go} />}
        {tab === "inbox" && <InboxTab screen={tab === "inbox" ? screen : null} goScreen={(s) => setScreen(s)} />}
        {tab === "account" && <AccountTab onLogout={onLogout} />}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    const token = localStorage.getItem("dishly_token");
    if (!token) { setAuthed(false); return; }
    try {
      await api.get("/app-admin/me");
      setAuthed(true);
    } catch {
      localStorage.removeItem("dishly_token");
      localStorage.removeItem(SESSION_KEY);
      setAuthed(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = async () => {
    try { await api.post("/app-admin/logout"); } catch { /* session already gone */ }
    localStorage.removeItem("dishly_token");
    localStorage.removeItem(SESSION_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="flex min-h-screen items-center justify-center bg-cream text-charcoal/50">Loading…</div>;
  }
  return authed ? <AdminShell onLogout={logout} /> : <LoginView onLogin={() => setAuthed(true)} />;
}
